import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  NativeSelect,
  SimpleGrid,
  Text,
  Button,
  Icon,
  useDisclosure,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster';
import { FaFilePdf, FaFileImage } from 'react-icons/fa';
import { CALENDARIO_FILTRO_OPTS, getCalendarioFiltroLabel } from '../utils/calendarioFiltros';
import {
  downloadCalendarioPdf,
  downloadCalendarioPng,
  loadBandaLogoAssetForCalendario,
} from '../utils/exportCalendario';

export default function CalendarioActividadesToolbar({
  filtro,
  onFiltroChange,
  actuaciones = [],
  ensayos = [],
  bandName = '',
  eventCount = 0,
  currentMonth,
  calendarData,
  showCard = true,
  onShareControlsReady,
}) {
  
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const [exportLoading, setExportLoading] = useState(false);

  const canShare = true;

  useEffect(() => {
    onShareControlsReady?.({ onOpen, canShare });
  }, [onShareControlsReady, onOpen, canShare]);

  const monthLabel = currentMonth
    ? currentMonth.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
    : '';

  const handleExport = useCallback(async (format) => {
    setExportLoading(true);
    try {
      const bandLogoAsset = await loadBandaLogoAssetForCalendario();
      const params = {
        currentMonth: currentMonth || new Date(),
        calendarData: calendarData || { fechasOcupadas: [], fechasDisponibles: [] },
        actuaciones,
        ensayos,
        bandName,
        filtro,
        bandLogoAsset,
      };
      if (format === 'pdf') await downloadCalendarioPdf(params);
      else await downloadCalendarioPng(params);

      onClose();
      toaster.create({
        title: format === 'pdf' ? 'PDF generado' : 'Imagen generada',
        type: 'success',
        duration: 2500,
      });
    } catch {
      toaster.create({
        title: 'Error al exportar',
        description: 'No se pudo generar el archivo. Inténtalo de nuevo.',
        type: 'error',
        duration: 4000,
      });
    } finally {
      setExportLoading(false);
    }
  }, [actuaciones, ensayos, bandName, filtro, currentMonth, calendarData, onClose]);

  const content = (
    <Field.Root>
      <Field.Label fontSize="xs" color="gray.500" mb={1}>
        Mostrar en el calendario
      </Field.Label>
      <NativeSelect.Root>
        <NativeSelect.Field
          size="sm"
          value={filtro}
          onChange={(e) => onFiltroChange(e.target.value)}
          borderRadius="lg"
          bg="white">
          {CALENDARIO_FILTRO_OPTS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator />
      </NativeSelect.Root>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {filtro === 'nada'
          ? 'Solo disponibilidad y días ocupados.'
          : `${eventCount} evento${eventCount === 1 ? '' : 's'} con este filtro.`}
      </Text>
    </Field.Root>
  );

  return (
    <>
      {showCard ? <CardLikeToolbar>{content}</CardLikeToolbar> : content}

      <Dialog.Root open={isOpen} placement='center' size='sm' onOpenChange={e => {
        if (!e.open) {
          onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="md" py={3}>Compartir calendario</Dialog.Header>
              <Dialog.CloseTrigger disabled={exportLoading} size="sm" />
              <Dialog.Body pb={4} pt={0}>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  Se exportará el mes visible
                  {monthLabel ? ` (${monthLabel})` : ''} con el filtro «{getCalendarioFiltroLabel(filtro)}».
                  {filtro !== 'nada' && eventCount > 0
                    ? ` ${eventCount} evento${eventCount === 1 ? '' : 's'} en el calendario.`
                    : ' Incluye disponibilidad y días ocupados.'}
                </Text>
                <SimpleGrid columns={2} gap={3}>
                  <Button
                    size="sm"
                    h="auto"
                    py={3}
                    px={2}
                    flexDirection="column"
                    variant="outline"
                    colorPalette="red"
                    onClick={() => handleExport('pdf')}
                    loading={exportLoading}
                    loadingText="Generando…"
                  >
                    <Icon boxSize={4} mb={1} asChild><FaFilePdf /></Icon>
                    PDF
                    <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                      Con cabecera Cadencya
                    </Text>
                  </Button>
                  <Button
                    size="sm"
                    h="auto"
                    py={3}
                    px={2}
                    flexDirection="column"
                    variant="outline"
                    colorPalette="purple"
                    onClick={() => handleExport('png')}
                    loading={exportLoading}
                    loadingText="Generando…"
                  >
                    <Icon boxSize={4} mb={1} asChild><FaFileImage /></Icon>
                    PNG
                    <Text fontSize="2xs" fontWeight="normal" color="gray.500" mt={0.5} lineHeight="short">
                      Imagen para compartir
                    </Text>
                  </Button>
                </SimpleGrid>
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}

function CardLikeToolbar({ children }) {
  return (
    <Box
      w="full"
      bg="purple.50"
      border="2px solid"
      borderColor="purple.200"
      rounded="xl"
      p={{ base: 3, md: 4 }}
      mb={4}
    >
      {children}
    </Box>
  );
}
